import { getFunctions, httpsCallable } from 'firebase/functions';
import type { PortalDeliverableApproval, PortalReleaseTarget, PortalStage } from '../types';
import { portalStageIndex } from './portalStageEngine';

export type RecordPortalDeliverableApprovalInput = {
  portalId: string;
  dealId: string;
  deliverableType: 'plans' | 'model' | 'concept';
  signature: { dataUrl: string; signedBy: string };
  releaseId?: string;
  bundleId?: string;
  releaseVersionIndex?: number;
  /** Binds approval to a static image URL (concept preview image). */
  assetUrl?: string;
  /** All releases covered by this approval (bundle members) — stamped per-release for the trail. */
  releaseIds?: string[];
};

export async function recordPortalDeliverableApproval(
  input: RecordPortalDeliverableApprovalInput,
): Promise<void> {
  const fn = httpsCallable<RecordPortalDeliverableApprovalInput, { ok: boolean }>(
    getFunctions(undefined, 'us-central1'),
    'recordPortalDeliverableApproval',
  );
  await fn(input);
}

export async function resendPortalDeliverableApprovalNotifications(input: {
  portalId: string;
  dealId: string;
  deliverableType: 'plans' | 'model' | 'concept';
}): Promise<void> {
  const fn = httpsCallable<
    { portalId: string; dealId: string; deliverableType: 'plans' | 'model' | 'concept' },
    { ok: boolean }
  >(getFunctions(undefined, 'us-central1'), 'resendPortalDeliverableApprovalNotifications');
  await fn(input);
}

export function isDeliverableApproved(
  approval: PortalDeliverableApproval | undefined,
  currentReleaseId?: string,
): boolean {
  if (!approval || approval.status !== 'approved') return false;
  if (currentReleaseId && approval.releaseId && approval.releaseId !== currentReleaseId) {
    return false;
  }
  return true;
}

export function formatApprovalBadge(approval: PortalDeliverableApproval | undefined): string | null {
  if (!approval || approval.status !== 'approved') return null;
  const date = approval.approvedAt
    ? approval.approvedAt instanceof Date
      ? approval.approvedAt
      : (approval.approvedAt as { toDate?: () => Date })?.toDate?.()
    : null;
  const dateStr = date
    ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : '';
  const ver =
    typeof approval.releaseVersionIndex === 'number' ? ` v${approval.releaseVersionIndex}` : '';
  return `Approved${ver}${dateStr ? ` · ${dateStr}` : ''} by ${approval.approvedByName}`;
}

export function shouldArchivePriorApproval(
  existing: Pick<PortalDeliverableApproval, 'status' | 'releaseId' | 'releaseVersionIndex'> | undefined,
  next: { releaseId?: string; releaseVersionIndex?: number },
): boolean {
  if (!existing || existing.status !== 'approved') return false;
  const existingRid = (existing.releaseId || '').trim();
  const nextRid = (next.releaseId || '').trim();
  if (existingRid && nextRid) return existingRid !== nextRid;
  if (
    typeof existing.releaseVersionIndex === 'number' &&
    typeof next.releaseVersionIndex === 'number'
  ) {
    return existing.releaseVersionIndex !== next.releaseVersionIndex;
  }
  return existingRid !== nextRid;
}

export function collectDeliverableApprovals(
  current?: PortalDeliverableApproval,
  history?: PortalDeliverableApproval[],
): PortalDeliverableApproval[] {
  const list: PortalDeliverableApproval[] = [];
  if (current?.status === 'approved') list.push(current);
  if (history) {
    for (let i = 0; i < history.length; i++) {
      const approval = history[i];
      if (approval?.status === 'approved') list.push(approval);
    }
  }
  return list;
}

export type PortalApprovalTargetGroup = {
  plans: PortalDeliverableApproval[];
  model: PortalDeliverableApproval[];
  concept: PortalDeliverableApproval[];
};

export function emptyPortalApprovalTargetGroup(): PortalApprovalTargetGroup {
  return { plans: [], model: [], concept: [] };
}

export function groupPortalDealApprovals(project: {
  plansApproval?: PortalDeliverableApproval;
  plansApprovalHistory?: PortalDeliverableApproval[];
  modelApproval?: PortalDeliverableApproval;
  modelApprovalHistory?: PortalDeliverableApproval[];
  conceptApproval?: PortalDeliverableApproval;
  conceptApprovalHistory?: PortalDeliverableApproval[];
}): PortalApprovalTargetGroup {
  return {
    plans: collectDeliverableApprovals(project.plansApproval, project.plansApprovalHistory),
    model: collectDeliverableApprovals(project.modelApproval, project.modelApprovalHistory),
    concept: collectDeliverableApprovals(project.conceptApproval, project.conceptApprovalHistory),
  };
}

export function releasePortalTarget(release: {
  portalTarget?: PortalReleaseTarget;
  streamKind: string;
}): PortalReleaseTarget {
  return release.portalTarget || (release.streamKind === 'three_d_html' ? 'model' : 'engineered_drawings');
}

export function findApprovalForRelease(
  approvals: PortalDeliverableApproval[],
  release: { id?: string; releaseVersionIndex: number },
): PortalDeliverableApproval | undefined {
  return approvals.find(approval => {
    const releaseId = (release.id || '').trim();
    const approvalReleaseId = (approval.releaseId || '').trim();
    if (releaseId && approvalReleaseId && approvalReleaseId === releaseId) return true;
    return (
      typeof approval.releaseVersionIndex === 'number' &&
      approval.releaseVersionIndex === release.releaseVersionIndex
    );
  });
}

export function findClientApprovalForFileReleases(
  grouped: PortalApprovalTargetGroup,
  releases: Array<{
    id?: string;
    releaseVersionIndex: number;
    portalTarget?: PortalReleaseTarget;
    streamKind: string;
  }>,
): PortalDeliverableApproval | undefined {
  for (const release of releases) {
    const target = releasePortalTarget(release);
    const list =
      target === 'model' ? grouped.model : target === 'concept_elevations' ? grouped.concept : grouped.plans;
    const found = findApprovalForRelease(list, release);
    if (found) return found;
  }
  return undefined;
}

export function findApprovalForBundle(
  approvals: PortalDeliverableApproval[],
  bundle: { id?: string; releaseIds: string[]; primaryReleaseId?: string; releaseVersionIndex: number },
): PortalDeliverableApproval | undefined {
  return approvals.find(approval => {
    if (approval.releaseId) {
      return (
        bundle.releaseIds.indexOf(approval.releaseId) !== -1 ||
        approval.releaseId === bundle.primaryReleaseId
      );
    }
    if (approval.bundleId && bundle.id && approval.bundleId === bundle.id) return true;
    return (
      typeof approval.releaseVersionIndex === 'number' &&
      approval.releaseVersionIndex === bundle.releaseVersionIndex
    );
  });
}

export function nextApprovalWithHistory(
  existing: PortalDeliverableApproval | undefined,
  history: PortalDeliverableApproval[] | undefined,
  next: PortalDeliverableApproval,
): { current: PortalDeliverableApproval; history: PortalDeliverableApproval[] } {
  const prior = history ? history.slice() : [];
  if (existing && shouldArchivePriorApproval(existing, next)) {
    prior.push(existing);
  }
  return { current: next, history: prior };
}

export function filterReleasesByTarget(
  releases: { portalTarget?: PortalReleaseTarget; streamKind: string }[],
  target: PortalReleaseTarget,
): typeof releases {
  return releases.filter(r => {
    const t = r.portalTarget || (r.streamKind === 'three_d_html' ? 'model' : 'engineered_drawings');
    return t === target;
  });
}

export interface ConceptDeliverablesApprovalGateInput {
  modelUrl?: string;
  modelImageUrl?: string;
  conceptElevationUrl?: string;
  modelStepSkipped?: boolean;
  /** Staff override: keep concept review off after a quote is sent. Passed through the signing gate. */
  conceptApprovalFlowDisabled?: boolean;
  modelApproval?: PortalDeliverableApproval;
  modelApprovalHistory?: PortalDeliverableApproval[];
  conceptApproval?: PortalDeliverableApproval;
  conceptApprovalHistory?: PortalDeliverableApproval[];
  modelReleaseId?: string;
  modelReleaseVersionIndex?: number;
  conceptReleaseId?: string;
  conceptReleaseVersionIndex?: number;
}

function approvalCoversRelease(
  current: PortalDeliverableApproval | undefined,
  history: PortalDeliverableApproval[] | undefined,
  release: { id?: string; releaseVersionIndex?: number },
): boolean {
  const rid = (release.id || '').trim();
  const hasPointer = !!rid || typeof release.releaseVersionIndex === 'number';
  const all = collectDeliverableApprovals(current, history);
  if (!hasPointer) return all.length > 0;
  return !!findApprovalForRelease(all, {
    id: release.id,
    releaseVersionIndex: release.releaseVersionIndex ?? 0,
  });
}

export function needsInteractiveModelDeliverable(input: ConceptDeliverablesApprovalGateInput): boolean {
  return !!input.modelUrl?.trim() && !input.modelStepSkipped;
}

/** Static concept preview image when no interactive 3D model is published. */
export function needsPreviewImageDeliverable(input: ConceptDeliverablesApprovalGateInput): boolean {
  return !!input.modelImageUrl?.trim() && !needsInteractiveModelDeliverable(input) && !input.modelStepSkipped;
}

export function needsConceptPdfDeliverable(input: ConceptDeliverablesApprovalGateInput): boolean {
  return !!(input.conceptElevationUrl?.trim() || input.conceptReleaseId);
}

export function isPreviewImageApproved(
  approval: PortalDeliverableApproval | undefined,
  imageUrl?: string,
): boolean {
  if (!approval || approval.status !== 'approved') return false;
  const url = (imageUrl || '').trim();
  if (!url) return false;
  if (approval.assetUrl?.trim()) return approval.assetUrl.trim() === url;
  return false;
}

export function isPreviewImageApprovedForGate(input: ConceptDeliverablesApprovalGateInput): boolean {
  const url = (input.modelImageUrl || '').trim();
  if (!url) return false;
  const all = collectDeliverableApprovals(input.modelApproval, input.modelApprovalHistory);
  return all.some(a => isPreviewImageApproved(a, url));
}

export function isInteractiveModelApproved(
  approval: PortalDeliverableApproval | undefined,
  releaseId?: string,
): boolean {
  return isDeliverableApproved(approval, releaseId);
}

export function isInteractiveModelApprovedForGate(input: ConceptDeliverablesApprovalGateInput): boolean {
  return approvalCoversRelease(input.modelApproval, input.modelApprovalHistory, {
    id: input.modelReleaseId,
    releaseVersionIndex: input.modelReleaseVersionIndex,
  });
}

export function isConceptPdfApprovedForGate(input: ConceptDeliverablesApprovalGateInput): boolean {
  return approvalCoversRelease(input.conceptApproval, input.conceptApprovalHistory, {
    id: input.conceptReleaseId,
    releaseVersionIndex: input.conceptReleaseVersionIndex,
  });
}

/** True when the project has concept-phase files for the client to review. */
export function hasConceptDeliverables(input: ConceptDeliverablesApprovalGateInput): boolean {
  return (
    needsInteractiveModelDeliverable(input) ||
    needsPreviewImageDeliverable(input) ||
    needsConceptPdfDeliverable(input)
  );
}

/** Fill model / concept pointers from portal file releases when the project row is stale. */
export function mergePortalReleasesIntoSigningGateInput(
  project: ConceptDeliverablesApprovalGateInput,
  releases: Array<{
    id?: string;
    releasedUrl?: string;
    streamKind?: string;
    portalTarget?: string;
    releaseVersionIndex?: number;
  }>,
): ConceptDeliverablesApprovalGateInput {
  const latest = (
    list: Array<{ id?: string; releasedUrl?: string; releaseVersionIndex?: number }>,
  ) =>
    list.reduce<(typeof list)[0] | undefined>((best, r) => {
      if (!best) return r;
      return (r.releaseVersionIndex ?? 0) > (best.releaseVersionIndex ?? 0) ? r : best;
    }, undefined);

  const model = latest(
    releases.filter(r => r.portalTarget === 'model' || r.streamKind === 'three_d_html'),
  );
  const concept = latest(releases.filter(r => r.portalTarget === 'concept_elevations'));
  return {
    ...project,
    modelUrl: project.modelUrl?.trim() || model?.releasedUrl,
    modelReleaseId: model?.id || project.modelReleaseId,
    modelReleaseVersionIndex: model?.releaseVersionIndex ?? project.modelReleaseVersionIndex,
    conceptElevationUrl: project.conceptElevationUrl?.trim() || concept?.releasedUrl,
    conceptReleaseId: concept?.id || project.conceptReleaseId,
    conceptReleaseVersionIndex: concept?.releaseVersionIndex ?? project.conceptReleaseVersionIndex,
  };
}

export interface ProposalSigningGateInput extends ConceptDeliverablesApprovalGateInput {
  isRomPricing?: boolean;
  /**
   * Skip concept-deliverable blockers. Callers set this only when a client-visible quote
   * has the staff override. Draft quotes stay blocked.
   */
  skipConceptApproval?: boolean;
}

/** Quote the client can open: sent, opened, or signed. */
export function isQuoteVisibleToClient(status: string | null | undefined): boolean {
  return status === 'sent' || status === 'opened' || status === 'approved';
}

/**
 * Concept review and approve is on only after a quote is visible, unless staff turned it off.
 * Missing flag keeps today's behavior for projects that already have a sent quote.
 */
export function isConceptApprovalFlowEnabled(opts: {
  flowDisabled?: boolean;
  proposalStatus?: string | null;
}): boolean {
  if (opts.flowDisabled) return false;
  return isQuoteVisibleToClient(opts.proposalStatus);
}

/**
 * Concept Deliverables stops asking for a signature once the project is under
 * contract. Later model and drawing approvals live on the design approval card.
 * A stored Quote Signed stage alone does not count — staff can pin that before
 * the client signs.
 */
export function isConceptReviewRetired(opts: {
  proposalStatus?: string | null;
  currentStage?: PortalStage | string | null;
}): boolean {
  if (isPortalProposalSigned(opts.proposalStatus)) return true;
  const stage = opts.currentStage as PortalStage | null | undefined;
  if (!stage) return false;
  return portalStageIndex(stage) > portalStageIndex('quote-signed');
}

/** Signing skips concept approval only for a visible quote with the staff override. */
export function shouldSkipConceptApprovalForSigning(opts: {
  flowDisabled?: boolean;
  proposalStatus?: string | null;
}): boolean {
  return !!opts.flowDisabled && isQuoteVisibleToClient(opts.proposalStatus);
}

export type ProposalSigningBlockReason = 'rom_pricing' | 'no_deliverables' | 'deliverables_pending';

export interface ProposalSigningGateResult {
  canSign: boolean;
  reasons: ProposalSigningBlockReason[];
  blockers: string[];
}

export interface EvaluateProposalSigningGateOptions {
  /** When false, missing deliverables do not block (e.g. no linked portal). ROM still blocks. */
  requireDeliverables?: boolean;
}

/** True when the portal project has concept-phase deliverables that require client sign-off. */
export function conceptDeliverablesRequireApproval(
  input: ConceptDeliverablesApprovalGateInput,
): boolean {
  return hasConceptDeliverables(input);
}

/** Whether all required concept deliverables are approved (proposal may be signed). */
export function areConceptDeliverablesApprovedForSigning(
  input: ConceptDeliverablesApprovalGateInput,
): boolean {
  if (!conceptDeliverablesRequireApproval(input)) return true;

  const interactiveOk =
    !needsInteractiveModelDeliverable(input) ||
    isInteractiveModelApprovedForGate(input);
  const imageOk =
    !needsPreviewImageDeliverable(input) ||
    isPreviewImageApprovedForGate(input);
  const conceptOk =
    !needsConceptPdfDeliverable(input) ||
    isConceptPdfApprovedForGate(input);
  return interactiveOk && imageOk && conceptOk;
}

export function getConceptDeliverablesApprovalBlockers(
  input: ConceptDeliverablesApprovalGateInput,
): string[] {
  const blockers: string[] = [];

  if (needsInteractiveModelDeliverable(input) && !isInteractiveModelApprovedForGate(input)) {
    blockers.push('3D model');
  }
  if (needsPreviewImageDeliverable(input) && !isPreviewImageApprovedForGate(input)) {
    blockers.push('concept preview image');
  }
  if (needsConceptPdfDeliverable(input) && !isConceptPdfApprovedForGate(input)) {
    blockers.push('2D concept drawings');
  }
  return blockers;
}

/** Full client signing gate: ROM pricing, deliverables present, and deliverables approved. */
export function evaluateProposalSigningGate(
  input: ProposalSigningGateInput,
  options: EvaluateProposalSigningGateOptions = {},
): ProposalSigningGateResult {
  const requireDeliverables = options.requireDeliverables !== false && !input.skipConceptApproval;
  const reasons: ProposalSigningBlockReason[] = [];
  const blockers: string[] = [];

  if (input.isRomPricing) {
    return {
      canSign: false,
      reasons: ['rom_pricing'],
      blockers: ['final pricing (this quote is preliminary ROM)'],
    };
  }

  if (requireDeliverables) {
    if (!hasConceptDeliverables(input)) {
      reasons.push('no_deliverables');
      blockers.push('concept deliverables (3D model, concept preview, and/or 2D concept drawings)');
    } else if (!areConceptDeliverablesApprovedForSigning(input)) {
      reasons.push('deliverables_pending');
      blockers.push(...getConceptDeliverablesApprovalBlockers(input));
    }
  }

  return {
    canSign: reasons.length === 0,
    reasons,
    blockers,
  };
}

export function proposalSigningGateTitle(result: ProposalSigningGateResult): string {
  const { reasons } = result;
  if (reasons.length === 0) return '';
  if (reasons.includes('rom_pricing')) {
    return 'ROM pricing only';
  }
  if (reasons.includes('no_deliverables') && !reasons.includes('deliverables_pending')) {
    return 'Concept deliverables required before signing';
  }
  if (reasons.includes('deliverables_pending')) {
    return 'Approve concept deliverables before signing';
  }
  return 'Proposal not ready for signing';
}

export function proposalSigningGateLeadCopy(result: ProposalSigningGateResult): string {
  if (result.reasons.includes('rom_pricing')) {
    return 'This quote is preliminary ROM pricing only. It cannot be signed until final numbers are published.';
  }
  if (result.reasons.includes('no_deliverables')) {
    return 'You can review this proposal now. Signing unlocks once we release your concept deliverables.';
  }
  return 'You can review this proposal now. Signing unlocks after you approve:';
}

/** True only when the proposal builder doc is actually signed — not when the portal stage is quote-signed. */
export function isPortalProposalSigned(status: string | null | undefined): boolean {
  return status === 'approved';
}

/** Portal quote CTA / walkthrough: offer Sign Proposal only when signing is unlocked. */
export function canOfferPortalSignProposal(input: {
  canSign: boolean;
  proposalVisible: boolean;
  hasProposalSlug: boolean;
  isStakeholderViewer: boolean;
  isSampleGcPortal?: boolean;
  alreadySigned: boolean;
}): boolean {
  return (
    input.canSign &&
    input.proposalVisible &&
    input.hasProposalSlug &&
    !input.isStakeholderViewer &&
    !input.isSampleGcPortal &&
    !input.alreadySigned
  );
}

/** Header line for an already-signed deliverable, or null when there is nothing to show. */
export function formatPortalApprovalCaption(
  approval: Pick<PortalDeliverableApproval, 'status' | 'approvedByName' | 'approvedAt'> | null | undefined,
): string | null {
  if (!approval || approval.status !== 'approved') return null;
  const name = (approval.approvedByName || '').trim();
  const at =
    approval.approvedAt instanceof Date && !Number.isNaN(approval.approvedAt.getTime())
      ? approval.approvedAt
      : null;
  const when = at
    ? at.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';
  if (name && when) return `Approved by ${name} on ${when}.`;
  if (name) return `Approved by ${name}.`;
  if (when) return `Approved on ${when}.`;
  return null;
}

/** Client-facing scope of concept-model approval — not a lock on shop drawings. */
export const CONCEPT_MODEL_APPROVAL_DISCLAIMER =
  "Approving this concept model does not lock in final dimensions or joinery. It confirms the model is a reasonable approximation of what Leiper's Creek Timber Frames will provide, based on the plans you have shared.";

/** Deep-link the portal to the Concept Deliverables / 3D model step. */
export const PORTAL_FOCUS_CONCEPT_MODEL = 'concept-model';

export function portalConceptModelReturnPath(
  slug: string,
  projectId?: string | null,
): string {
  const q = new URLSearchParams({ focus: PORTAL_FOCUS_CONCEPT_MODEL });
  if (projectId?.trim()) q.set('project', projectId.trim());
  return `/portal/${slug}?${q.toString()}`;
}

/** Concept files are released and still need approval — send the client to review them. */
export function shouldRedirectProposalSignToConceptModel(
  reasons: ProposalSigningBlockReason[],
): boolean {
  return reasons.includes('deliverables_pending') && !reasons.includes('no_deliverables');
}

export function portalProposalCtaLabel(input: {
  proposalSigned: boolean;
  canOfferSign: boolean;
}): 'View Signed Proposal' | 'Sign Proposal' | 'View Proposal' {
  if (input.proposalSigned) return 'View Signed Proposal';
  if (input.canOfferSign) return 'Sign Proposal';
  return 'View Proposal';
}
